import { useState, useRef, useEffect, useCallback } from "react";
import "./AttachmentOptions.css";
import { getFriends } from "../../../functions/user";

export default function AttachmentOptions({ 
  isOpen, 
  onClose, 
  onFileSelect,
  currentUser 
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const overlayRef = useRef(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [, setContacts] = useState([]);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Contacts data would be loaded from an API in a real app
  useEffect(() => {
    if (selectedOption === 'contacts') {
      // No dummy data - contacts array remains empty
      setContacts([]);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(null);
      setShowFriendsList(false);
    }
  }, [isOpen]);

  const renderIcon = (optionId) => {
    const iconStyle = {
      width: '48px',
      height: '48px',
      marginBottom: '8px'
    };

    switch (optionId) {
      case 'documents':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" fill="#9B7EDE" stroke="#7B5FC7" strokeWidth="1.5"/>
            <path d="M14 2V8H20" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="8" y1="12" x2="16" y2="12" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="8" y1="16" x2="16" y2="16" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="8" y1="20" x2="12" y2="20" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'gallery':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" fill="#D4A574" stroke="#B8956A" strokeWidth="1.5"/>
            <rect x="5" y="5" width="14" height="10" rx="1" fill="#87CEEB" stroke="#5BA3D0" strokeWidth="1"/>
            <circle cx="18" cy="7" r="2" fill="#FFD700"/>
            <ellipse cx="7" cy="9" rx="1.5" ry="0.8" fill="#FFFFFF" opacity="0.8"/>
            <ellipse cx="10" cy="8.5" rx="1.2" ry="0.6" fill="#FFFFFF" opacity="0.8"/>
            <path d="M5 15L5 13C5 12.5 5.5 12 6 12L8 12C8.5 12 9 12.5 9 13L9 15" fill="#4CAF50" stroke="#2E7D32" strokeWidth="1"/>
            <path d="M11 15L11 13.5C11 13 11.5 12.5 12 12.5L14 12.5C14.5 12.5 15 13 15 13.5L15 15" fill="#4CAF50" stroke="#2E7D32" strokeWidth="1"/>
            <path d="M17 15L17 14C17 13.5 17.5 13 18 13L19 13C19.5 13 20 13.5 20 14L20 15" fill="#4CAF50" stroke="#2E7D32" strokeWidth="1"/>
          </svg>
        );
      case 'camera':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="7" width="20" height="14" rx="2" fill="#D4C5E8" stroke="#9B7EDE" strokeWidth="1.5"/>
            <path d="M8 7V5C8 4.46957 8.21071 3.96086 8.58579 3.58579C8.96086 3.21071 9.46957 3 10 3H14C14.5304 3 15.0391 3.21071 15.4142 3.58579C15.7893 3.96086 16 4.46957 16 5V7" stroke="#9B7EDE" strokeWidth="1.5"/>
            <circle cx="12" cy="13" r="3" fill="#9B7EDE" stroke="#7B5FC7" strokeWidth="1"/>
            <circle cx="12" cy="13" r="1.5" fill="#7B5FC7"/>
          </svg>
        );
      case 'audio':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18V5L21 3V16" stroke="#9B7EDE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M6 21C7.65685 21 9 19.6569 9 18C9 16.3431 7.65685 15 6 15C4.34315 15 3 16.3431 3 18C3 19.6569 4.34315 21 6 21Z" fill="#9B7EDE" stroke="#7B5FC7" strokeWidth="1.5"/>
            <path d="M18 19C19.6569 19 21 17.6569 21 16C21 14.3431 19.6569 13 18 13C16.3431 13 15 14.3431 15 16C15 17.6569 16.3431 19 18 19Z" fill="#9B7EDE" stroke="#7B5FC7" strokeWidth="1.5"/>
            <path d="M9 8L21 6" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );
      case 'contacts':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="9" cy="7" r="4" fill="#7B5FC7" stroke="#5A3FA3" strokeWidth="1.5"/>
            <circle cx="15" cy="7" r="4" fill="#7B5FC7" stroke="#5A3FA3" strokeWidth="1.5"/>
            <path d="M3 21C3 17.134 6.13401 14 10 14C13.866 14 17 17.134 17 21" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
            <path d="M7 21C7 18.7909 8.79086 17 11 17C13.2091 17 15 18.7909 15 21" stroke="#7B5FC7" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
          </svg>
        );
      case 'location':
        return (
          <svg style={iconStyle} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="#FF69B4" stroke="#FF1493" strokeWidth="1.5"/>
            <circle cx="12" cy="9" r="3" fill="#FFFFFF" stroke="#FF1493" strokeWidth="1"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const attachmentOptions = [
    {
      id: 'documents',
      label: 'Documents',
      description: 'PDF, DOC, TXT files'
    },
    {
      id: 'gallery',
      label: 'Gallery',
      description: 'Photos and videos'
    },
    {
      id: 'camera',
      label: 'Camera',
      description: 'Take a photo'
    },
    {
      id: 'audio',
      label: 'Audio',
      description: 'Music and audio files'
    },
    {
      id: 'contacts',
      label: 'Contacts',
      description: 'Share contact info'
    },
    {
      id: 'location',
      label: 'Location',
      description: 'Share your location'
    }
  ];

  // Load friends list
  useEffect(() => {
    const loadFriendsList = async () => {
      if (selectedOption === 'contacts' && !friends.length) {
        setLoading(true);
        try {
          const friendsList = await getFriends(currentUser.token);
          if (Array.isArray(friendsList)) {
            // Filter out the current user and map the friends to the expected format
            const formattedFriends = friendsList
              .filter(friend => friend._id !== currentUser.id) // Exclude current user
              .map(friend => ({
                _id: friend._id,
                name: friend.name || `${friend.first_name} ${friend.last_name || ''}`.trim(),
                picture: friend.picture || '/images/default_profile.png',
                avatar: friend.picture || '/images/default_profile.png',
                username: friend.username,
                phone: friend.phoneNumber || friend.phone || '',
              }));
            setFriends(formattedFriends);
          } else {
            console.error('Failed to load friends:', friendsList);
            setFriends([]);
          }
        } catch (err) {
          console.error('Error loading friends:', err);
          setFriends([]);
        } finally {
          setLoading(false);
        }
      }
    };
    loadFriendsList();
  }, [selectedOption, currentUser.token, friends.length, currentUser.id]);

  const handleOptionClick = (optionId) => {
    switch (optionId) {
      case 'documents':
        fileInputRef.current?.click();
        return; // Don't set selected option for documents
      case 'camera':
        cameraInputRef.current?.click();
        return; // Don't set selected option for camera
      case 'audio':
        audioInputRef.current?.click();
        return; // Don't set selected option for audio
      case 'contacts':
        setShowFriendsList(true);
        setSelectedOption('contacts');
        return;
      case 'gallery':
        // Show file picker directly for gallery
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = 'image/*,video/*';
        input.onchange = (e) => {
          const files = Array.from(e.target.files);
          if (files.length > 0) {
            onFileSelect(files);
            onClose(); // Close the attachment options after selection
            setSelectedOption(null); // Reset selected option
          }
        };
        input.click();
        return; // Don't set selected option for gallery
      default:
        setSelectedOption(optionId);
    }
  };


  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCapturedImage(null);
  }, [cameraStream]);

  // Capture image from camera
  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob and then to file
      canvas.toBlob((blob) => {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setCapturedImage(file);
      }, 'image/jpeg', 0.8); // 0.8 quality for smaller file size
    }
  };

  // Handle file selection from gallery or camera
  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Reset the input value to allow selecting the same file again
    e.target.value = '';

    // For documents and gallery, just pass them directly to the parent and close
    if (type === 'documents' || type === 'gallery') {
      onFileSelect(files);
      onClose();
      setSelectedOption(null); // Reset selected option to prevent showing the container again
      return;
    }

    // For other file types, handle them as before
    switch (type) {
      // gallery case removed as it's now handled above
      case 'camera':
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (event) => {
            onFileSelect([event.target.result]);
            onClose();
          };
          reader.readAsDataURL(files[0]);
        }
        break;
      case 'audio':
        onFileSelect(files);
        onClose();
        break;
      default:
        break;
    }
  };

  const handleGalleryUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,video/*';
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        onFileSelect(files);
        onClose(); // Close the attachment options after selection
        setSelectedOption(null); // Reset selected option
      }
    };
    input.click();
  };

  // Handle sending gallery files
  const handleSendGalleryFiles = () => {
    if (galleryFiles.length > 0) {
      onFileSelect(galleryFiles);
      setGalleryFiles([]);
      onClose();
      setSelectedOption(null); // Reset selected option to prevent showing the container again
    }
  };

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const renderOptionContent = () => {
    switch (selectedOption) {
      case 'camera':
        return (
          <div className="camera_content">
            <div className="content_header">
              <button className="back_btn" onClick={() => {
                stopCamera();
                setSelectedOption(null);
              }}>
                ← Back
              </button>
              <h3>Take Photo</h3>
            </div>
            <div className="camera_preview">
              {!capturedImage ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="camera_video"
                    onLoadedMetadata={() => {
                      // Start playing the video when metadata is loaded
                      if (videoRef.current) {
                        videoRef.current.play().catch(err => {
                          console.error('Error playing video:', err);
                        });
                      }
                    }}
                  />
                  <div className="camera_controls">
                    <button 
                      className="capture_btn"
                      onClick={captureImage}
                      title="Take photo"
                    >
                      ●
                    </button>
                  </div>
                </>
              ) : (
                <div className="captured_preview">
                  <img 
                    src={URL.createObjectURL(capturedImage)} 
                    alt="Captured" 
                    className="captured_image"
                  />
                  <div className="capture_actions">
                    <button 
                      className="retry_btn"
                      onClick={() => setCapturedImage(null)}
                    >
                      Retake
                    </button>
                    <button 
                      className="send_btn"
                      onClick={() => {
                        onFileSelect([capturedImage]);
                        stopCamera();
                        onClose();
                      }}
                    >
                      Send
                    </button>
                  </div>
                </div>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        );

      case 'gallery':
        return (
          <div className="attachment_content">
            <div className="content_header">
              <button className="back_btn" onClick={() => setSelectedOption(null)}>
                ← Back
              </button>
              <h3>Gallery</h3>
            </div>
            <div className="gallery_list">
              {galleryFiles.length > 0 ? (
                galleryFiles.map((file, index) => {
                  const isImage = file.type.startsWith('image/');
                  const fileSize = (file.size / (1024 * 1024)).toFixed(1);
                  const fileDate = new Date(file.lastModified).toLocaleDateString();
                  
                  return (
                    <div 
                      key={index} 
                      className="gallery_list_item"
                    >
                      <div className="gallery_item_icon">
                        <span className="notranslate">{isImage ? '🖼️' : '🎥'}</span>
                      </div>
                      <div className="gallery_item_info">
                        <span className="gallery_item_name">{file.name}</span>
                        <span className="gallery_item_details">
                          {fileSize} MB • {fileDate}
                        </span>
                      </div>
                      <button 
                        className="gallery_item_select"
                        onClick={() => {
                          onFileSelect([file]);
                          onClose();
                          setSelectedOption(null); // Reset selected option to prevent showing the container again
                        }}
                      >
                        send
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="no_gallery_items">
                  <div className="no_gallery_icon notranslate">📷</div>
                  <p>No media selected</p>

                </div>
              )}
            </div>
            <div className="gallery_actions">
              <button className="upload_new_btn" onClick={handleGalleryUpload}>
               
              </button>
              {galleryFiles.length > 0 && (
                <button 
                  className="send_btn"
                  onClick={handleSendGalleryFiles}
                >
                  Send {galleryFiles.length} {galleryFiles.length === 1 ? 'Item' : 'Items'}
                </button>
              )}
            </div>
          </div>
        );

      case 'documents':
        return (
          <div className="attachment_content">
            <div className="content_header">
              <button className="back_btn" onClick={() => setSelectedOption(null)}>
                ← Back
              </button>
              <h3>Documents</h3>
            </div>
            <div className="documents_list">
              {documentFiles.length > 0 ? (
                documentFiles.map((file, index) => (
                  <div key={index} className="document_list_item">
                    <div className="document_item_icon">
                      📄
                    </div>
                    <div className="document_item_info">
                      <span className="document_item_name">{file.name}</span>
                      <span className="document_item_details">
                        {(file.size / (1024 * 1024)).toFixed(1)} MB • 
                        {new Date(file.lastModified).toLocaleDateString()}
                      </span>
                    </div>
                    <button 
                      className="document_item_remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedFiles = [...documentFiles];
                        updatedFiles.splice(index, 1);
                        setDocumentFiles(updatedFiles);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <div className="no_documents">
                  <div className="no_documents_icon">📄</div>
              
                  <p className="hint">Click 'Add Files' to select from your device</p>
                </div>
              )}
            </div>
            <div className="document_upload_actions">
              <button 
                className="upload_new_btn" 
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.accept = '.pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';
                  input.onchange = (e) => {
                    const files = Array.from(e.target.files);
                    setDocumentFiles(prev => [...prev, ...files]);
                  };
                  input.click();
                }}
              >
                Add Files
              </button>
              {documentFiles.length > 0 && (
                <button 
                  className="send_documents_btn"
                  onClick={() => {
                    onFileSelect(documentFiles);
                    onClose();
                  }}
                >
                  Send {documentFiles.length} File{documentFiles.length > 1 ? 's' : ''}
                </button>
              )}
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="attachment_content">
            <div className="content_header">
              <button className="back_btn" onClick={() => setSelectedOption(null)}>
                ← Back
              </button>
              <h3>Audio Files</h3>
            </div>
            <div className="audio_list">
              <div className="no_audio_files">
                No audio files found. Click below to upload.
              </div>
            </div>
            <button className="upload_new_btn" onClick={() => audioInputRef.current?.click()}>
               Upload 
            </button>
          </div>
        );

      case 'contacts':
        if (showFriendsList) {
          return (
            <div className="friends_list_container">
              <div className="content_header">
                <button 
                  className="back_btn" 
                  onClick={() => setShowFriendsList(false)}
                >
                  ← Back
                </button>
                <h3>Select a friend</h3>
              </div>
              <div className="friends_list">
                {loading ? (
                  <div className="loading_text">Loading friends...</div>
                ) : friends.length > 0 ? (
                  friends.map(friend => (
                    <div 
                      key={friend._id} 
                      className="friend_item"
                      onClick={() => {
                        onFileSelect([{ 
                          type: 'contact', 
                          contact: {
                            id: friend._id,
                            name: friend.name,
                            username: friend.username,
                            avatar: friend.avatar || friend.picture,
                            picture: friend.picture,
                            phone: friend.phone || '',
                          },
                          name: friend.name,
                          picture: friend.picture
                        }]);
                        onClose();
                      }}
                    >
                      <img 
                        src={friend.picture} 
                        alt={friend.name} 
                        className="friend_picture" 
                      />
                      <span className="friend_name">{friend.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="no_friends">No friends found</div>
                )}
              </div>
            </div>
          );
        }
        return null;

      default:
        return (
          <div className="attachment_options_grid">
            {attachmentOptions.map(option => (
              <div 
                key={option.id}
                className="attachment_option"
                data-option={option.id}
                onClick={() => handleOptionClick(option.id)}
              >
                <div className="option_icon">{renderIcon(option.id)}</div>
                <div className="option_label">{option.label}</div>
                <div className="option_description">{option.description}</div>
              </div>
            ))}
          </div>
        );
    }
  };

  // Add a class to the nearest .chat_window while this overlay is mounted
  useEffect(() => {
    if (!isOpen) return;
    const el = overlayRef.current;
    // overlayRef will be set after render; guard for safety
    if (!el) return;
    const chatWindow = el.closest('.chat_window');
    if (chatWindow) chatWindow.classList.add('attachment-options-open');
    return () => {
      if (chatWindow) chatWindow.classList.remove('attachment-options-open');
    };
  }, [isOpen]);

  // Only render attachment overlay when open and not selecting a document or gallery
  if (!isOpen) return null;

  // If we're only showing documents or gallery, don't render the overlay
  const isDirectSelection = selectedOption === 'documents' || selectedOption === 'gallery';
  
  if (isDirectSelection) {
    return (
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={selectedOption === 'documents' 
          ? ".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          : "image/*,video/*"}
        onChange={(e) => handleFileChange(e, selectedOption)}
        style={{ display: 'none' }}
      />
    );
  }

  return (
    <>
      <div className="attachment_options_backdrop" onClick={onClose}></div>
      <div className="attachment_options_overlay" ref={overlayRef}>
        <div className="attachment_options_container" onClick={(e) => e.stopPropagation()}>
          <div className="attachment_options_header">
            <h2>Attach</h2>
            <button className="close_btn" onClick={onClose}>×</button>
          </div>
          
          {renderOptionContent()}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        onChange={(e) => handleFileChange(e, 'documents')}
        style={{ display: 'none' }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileChange(e, 'camera')}
        style={{ display: 'none' }}
      />
      <input
        ref={audioInputRef}
        type="file"
        multiple
        accept="audio/*"
        onChange={(e) => handleFileChange(e, 'audio')}
        style={{ display: 'none' }}
      />
    </>
  );
} 
